/**
 * What a person may do with somebody else's account.
 *
 * The server decides all of this again — these are the same rules, worked out
 * on screen so a manager is not offered a button that will refuse them. An
 * interface that lets you try and then says no teaches people that half of it
 * does not work.
 */

/** Roles that can do everything, including creating more of themselves. */
const RESTRICTED = ['super_admin', 'owner'];

export function isOwner(user) {
  return user?.roles?.some((role) => RESTRICTED.includes(role)) ?? false;
}

/**
 * May this person clear a staff member's account?
 *
 * A cashier can put a meal on somebody's tab, but clearing what is owed writes
 * off money the shop is due — so it is held to the owner and the branch
 * manager, the people answerable for the takings.
 */
const CAN_SETTLE = ['super_admin', 'owner', 'branch_manager'];
export function canSettleAccounts(user) {
  return user?.roles?.some((role) => CAN_SETTLE.includes(role)) ?? false;
}

/**
 * Is what was typed an amount that can be cleared off a tab owing `owes`?
 *
 * More than nothing, and no more than is owed — the server would quietly cap
 * an overpayment at the balance, and a figure the screen changed without saying
 * so is one somebody later swears they never entered.
 */
export function clearable(typed, owes) {
  const text = String(typed ?? '').trim();
  if (!text) return { ok: false, reason: 'Enter an amount' };
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return { ok: false, reason: 'Numbers only, like 1500' };

  const amount = Number(text);
  if (!(amount > 0)) return { ok: false, reason: 'More than nothing' };
  if (amount > owes + 0.005) return { ok: false, reason: 'That is more than is owed' };

  return { ok: true, amount, left: Math.max(0, Math.round((owes - amount) * 100) / 100) };
}

/**
 * Would deactivating this person lock the person doing it out?
 *
 * The one mistake that cannot be undone from inside the application, so it is
 * refused rather than confirmed.
 */
export function canDeactivate(actor, target) {
  if (!target?.is_active) return { ok: false, reason: 'Already on hold' };
  if (actor?.id === target?.id) return { ok: false, reason: 'This is your own account' };
  return { ok: true };
}

/**
 * May this person be taken off the list?
 *
 * Anyone but yourself. Removing your own account hides the one login that could
 * put it back — the same trap as deactivating yourself, refused for the same
 * reason. Somebody already on hold can still be archived; the two are different
 * (one has stepped away, the other has gone).
 */
export function canArchive(actor, target) {
  if (actor?.id === target?.id) return { ok: false, reason: 'This is your own account' };
  return { ok: true };
}

/** Can this person take away their own last privileged role? No. */
export function canChangeOwnRoles(actor, target, chosenRoleNames) {
  if (actor?.id !== target?.id) return { ok: true };
  if (!isOwner(actor)) return { ok: true };

  return chosenRoleNames.some((name) => RESTRICTED.includes(name))
    ? { ok: true }
    : { ok: false, reason: 'You cannot take your own owner role away' };
}

/**
 * How to describe an account at a glance.
 *
 * Locked is separate from on hold: one is somebody who mistyped their password
 * five times and needs letting back in, the other is somebody who has left.
 * Treating them the same sends a manager looking for the wrong fix.
 */
export function accountState(person, now = Date.now()) {
  // Off the list entirely: distinct from on hold, which is still on the list.
  if (person?.archived_at) return { label: 'Removed', tone: 'bg-surface-3 text-ink-3' };
  if (!person?.is_active) return { label: 'On hold', tone: 'bg-surface-3 text-ink-3' };

  if (person.locked_until) {
    const until = new Date(String(person.locked_until).replace(' ', 'T') + 'Z').getTime();
    if (Number.isFinite(until) && until > now) {
      return { label: 'Locked out', tone: 'bg-warn-bg text-warn' };
    }
  }

  return { label: 'Active', tone: 'bg-good-bg text-good' };
}

/** A password nobody has to invent, for handing to somebody on their first day. */
export function suggestPassword() {
  // Words rather than symbols: this gets read aloud across a counter, and a
  // password somebody cannot repeat is one they write on the till.
  const words = [
    'orange',
    'kettle',
    'ladder',
    'garden',
    'pepper',
    'window',
    'copper',
    'basket',
    'lantern',
    'meadow',
    'pillow',
    'saddle',
  ];

  const pick = () => words[Math.floor(Math.random() * words.length)];
  const digits = String(Math.floor(Math.random() * 90) + 10);

  return `${pick()}-${pick()}-${digits}`;
}

/** `branch_manager` is how the database spells it, not how anyone reads it. */
export function readableRole(name) {
  return (
    {
      super_admin: 'Administrator',
      owner: 'Owner',
      branch_manager: 'Branch manager',
      accountant: 'Accountant',
      store_keeper: 'Store keeper',
      inventory_manager: 'Stock manager (old)',
      purchase_manager: 'Purchasing (old)',
      dispatcher: 'Dispatcher',
      cashier: 'Cashier',
      kitchen_staff: 'Kitchen',
      rider: 'Rider',
    }[name] ?? name
  );
}
